import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Settings, Shield, Zap, Cloud, AlertTriangle, 
  CheckCircle, RefreshCw, Server, Database
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLOUD_PROVIDERS = [
  { id: 'aws', name: 'Amazon Web Services', color: 'bg-orange-100 text-orange-700', icon: '🔶' },
  { id: 'azure', name: 'Microsoft Azure', color: 'bg-emerald-100 text-emerald-700', icon: '🔷' },
  { id: 'gcp', name: 'Google Cloud Platform', color: 'bg-red-100 text-red-700', icon: '🔴' },
  { id: 'snowflake', name: 'Snowflake', color: 'bg-emerald-100 text-emerald-700', icon: '❄️' }
];

const AdminSettings = () => {
  const { user, isAdmin, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [simulationEnabled, setSimulationEnabled] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      fetchConfig();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Get simulation config
      const simRes = await axios.get(`${API_URL}/api/simulation/config`, { headers });
      setSimulationEnabled(simRes.data.enabled || false);
      setSelectedProviders(simRes.data.providers || []);
    } catch (error) {
      console.error('Error fetching config:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProviderToggle = (providerId) => {
    setSelectedProviders(prev => {
      if (prev.includes(providerId)) {
        return prev.filter(p => p !== providerId);
      } else {
        return [...prev, providerId];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API_URL}/api/simulation/config`,
        {
          enabled: simulationEnabled,
          providers: selectedProviders
        },
        { headers }
      );
      
      toast.success(simulationEnabled 
        ? `Simulation enabled for: ${selectedProviders.join(', ').toUpperCase()}`
        : 'Simulation mode disabled'
      );
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickEnable = async (providers) => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API_URL}/api/simulation/enable`,
        providers,
        { headers }
      );
      
      setSimulationEnabled(true);
      setSelectedProviders(providers);
      toast.success(`Simulation enabled for: ${providers.join(', ').toUpperCase()}`);
    } catch (error) {
      console.error('Error enabling simulation:', error);
      toast.error('Failed to enable simulation');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API_URL}/api/simulation/disable`, {}, { headers });
      
      setSimulationEnabled(false);
      toast.success('Simulation mode disabled');
    } catch (error) {
      console.error('Error disabling simulation:', error);
      toast.error('Failed to disable simulation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-slate-200 rounded" />
          <div className="h-48 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h3 className="text-xl font-semibold text-red-700 mb-2">Admin Access Required</h3>
            <p className="text-red-600">This page is only accessible to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Settings className="w-7 h-7 text-slate-400" />
            Admin Settings
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Configure simulation mode and testing features
          </p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700">
          <Shield className="w-3 h-3 mr-1" />
          Admin Only
        </Badge>
      </div>

      {/* Simulation Mode Card */}
      <Card className={simulationEnabled ? 'border-2 border-amber-400' : ''}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Simulation Mode
              </CardTitle>
              <CardDescription>
                Simulate connected cloud providers for testing and demos
              </CardDescription>
            </div>
            <Switch
              checked={simulationEnabled}
              onCheckedChange={setSimulationEnabled}
              data-testid="simulation-toggle"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {simulationEnabled && (
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">Simulation Active</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Data shown in dashboards is simulated. A banner will appear on all pages to indicate simulation mode.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Provider Selection */}
          <div>
            <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3">
              Select Providers to Simulate
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {CLOUD_PROVIDERS.map(provider => (
                <div
                  key={provider.id}
                  onClick={() => handleProviderToggle(provider.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedProviders.includes(provider.id)
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{provider.icon}</span>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{provider.name}</p>
                        <p className="text-xs text-slate-400">{provider.id.toUpperCase()}</p>
                      </div>
                    </div>
                    {selectedProviders.includes(provider.id) && (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="outline"
              onClick={() => handleQuickEnable(['aws', 'azure', 'gcp', 'snowflake'])}
              disabled={saving}
              className="gap-2"
            >
              <Cloud className="w-4 h-4" />
              Enable All Providers
            </Button>
            <Button
              variant="outline"
              onClick={() => handleQuickEnable(['aws'])}
              disabled={saving}
              className="gap-2"
            >
              AWS Only
            </Button>
            {simulationEnabled && (
              <Button
                variant="outline"
                onClick={handleDisable}
                disabled={saving}
                className="gap-2 text-red-600 hover:bg-red-50"
              >
                Disable Simulation
              </Button>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={saving || (simulationEnabled && selectedProviders.length === 0)}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* What Gets Simulated */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What Gets Simulated?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
              <Server className="w-5 h-5 text-emerald-500 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">One-Click Fix</p>
                <p className="text-sm text-slate-400">Savings recommendations for selected providers</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
              <Database className="w-5 h-5 text-emerald-500 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Cloud Cost Dashboard</p>
                <p className="text-sm text-slate-400">Cost data and trends for demos</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4">
            Note: Simulation mode is for testing only. No real cloud operations are performed.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
